<template>
  <!-- Sin ámbitos solo se muestra en modo edición, para poder crear el primero -->
  <div v-if="allows_crud || profileScopes.length > 0">
    <table class="table table-sm table-bordered realization-scopes-table">
      <thead>
        <tr>
          <th :colspan="columnCount" class="text-center">
            {{ entityNamePlural.toUpperCase() }}
            <div class="float-right noprint" v-if="allows_crud">
              <ButtonMedium
                v-can="'mesh.add_egressprofilerealizationscope'"
                :tooltip_text="
                  $t('reusable.common.addTooltip', { entity: entityName })
                "
                :text_button="$t('reusable.common.add')"
                :click_button="openCreate"
                :icon="'plus'"
                :variant="'primary'"
              ></ButtonMedium>
            </div>
          </th>
        </tr>
        <tr>
          <th v-if="allows_crud" class="noprint" style="width: 1%"></th>
          <th class="w-25">{{ $t("reusable.common.name") }}</th>
          <th>{{ $t("reusable.common.description") }}</th>
          <th v-if="allows_crud" class="noprint" style="width: 1%"></th>
        </tr>
      </thead>
      <draggable
        v-if="profileScopes.length > 0"
        :value="profileScopes"
        :disabled="!canReorder"
        tag="tbody"
        handle=".handle"
        @input="saveOrder"
      >
        <tr
          v-for="realization_scope in profileScopes"
          :key="realization_scope.id"
        >
          <td
            v-if="allows_crud"
            class="text-center noprint"
            :class="{ 'handle grabbable': canReorder }"
            :title="$t('reusable.common.order')"
          >
            &#8597;
          </td>
          <td class="text-left">{{ realization_scope.name }}</td>
          <td class="text-left description-cell">
            {{ realization_scope.description }}
          </td>
          <td v-if="allows_crud" class="noprint">
            <div class="d-flex">
              <ButtonSmall
                v-can="'mesh.change_egressprofilerealizationscope'"
                :tooltip_text="
                  $t('reusable.common.editTooltip', { entity: entityName })
                "
                :click_button="() => openEdit(realization_scope)"
                :icon="'square'"
              ></ButtonSmall>
              <ButtonSmall
                v-can="'mesh.delete_egressprofilerealizationscope'"
                :tooltip_text="
                  $t('reusable.common.deleteTooltip', { entity: entityName })
                "
                :click_button="() => askForDelete(realization_scope.id)"
                :custom_class="`ml-2`"
                :icon="'trash'"
              ></ButtonSmall>
            </div>
          </td>
        </tr>
      </draggable>
      <tbody v-else>
        <tr>
          <td :colspan="columnCount" class="text-center">
            {{
              $t("reusable.common.noDataContextToShow", {
                entity: entityNamePlural,
              })
            }}
          </td>
        </tr>
      </tbody>
    </table>
    <BsModal
      v-model="show_form"
      :title="
        $t(
          editing_scope
            ? 'reusable.common.editModalTitle'
            : 'reusable.common.createModalTitle',
          { entity: entityName }
        )
      "
      size="lg"
    >
      <RealizationScopeForm
        v-if="show_form"
        :key="editing_scope ? editing_scope.id : 'new'"
        :realization_scope="editing_scope"
        :egress_profile_id="egress_profile_id"
        @created="show_form = false"
        @updated="show_form = false"
      ></RealizationScopeForm>
    </BsModal>
  </div>
</template>

<script setup>
// eslint-plugin-vue 6.x (anterior a <script setup>) no ve que estos bindings
// se usan en el template. Quitar el disable cuando se actualice el tooling.
/* eslint-disable no-unused-vars */
// Listado de Ámbitos de realización de un perfil de egreso (ver $usesRealizationScopes):
// se crean aquí, antes de las competencias, y luego se vinculan a ellas y, en la Matriz 2,
// a sus asignaturas. Los vínculos se ven en la tabla de competencias, no aquí. El orden
// solo sirve para listarlos: no se muestra y se cambia arrastrando las filas.
// Legacy (Bootstrap-Vue): ButtonSmall/ButtonMedium son los botones compartidos de la app.
// vuedraggable no depende de Bootstrap-Vue.
import { computed, getCurrentInstance, ref } from "vue";
import * as names from "@/store/names";
import { toast } from "@/utils/utils";
import draggable from "vuedraggable";
import {
  reorderedScopes,
  scopesOfEgressProfile,
} from "@/utils/realizationScopes";
import BsModal from "@/components/reusable/bs/BsModal";
import ButtonMedium from "@/components/reusable/Buttons/Medium/ButtonMedium";
import ButtonSmall from "@/components/reusable/Buttons/Small/ButtonSmall";
import RealizationScopeForm from "./RealizationScopeForm";

const props = defineProps({
  egress_profile_id: {
    type: Number,
    required: true,
  },
  allows_crud: {
    type: Boolean,
    default: false,
  },
});

const proxy = getCurrentInstance().proxy;
const store = proxy.$store;

const entityName = computed(() =>
  proxy.$getVisibleNames(
    "mesh.egressprofilerealizationscope",
    false,
    "Ámbito de realización"
  )
);
const entityNamePlural = computed(() =>
  proxy.$getVisibleNames(
    "mesh.egressprofilerealizationscope",
    true,
    "Ámbitos de realización"
  )
);
const canReorder = computed(
  () =>
    props.allows_crud &&
    proxy.$hasPermissions("mesh.change_egressprofilerealizationscope")
);
const columnCount = computed(() => (props.allows_crud ? 4 : 2));

const profileScopes = computed(() =>
  scopesOfEgressProfile(
    store.getters[names.REALIZATION_SCOPES],
    props.egress_profile_id
  )
);

// Se actualiza el store al tiro para que la fila no salte de vuelta mientras se guarda.
function saveOrder(ordered_scopes) {
  reorderedScopes(ordered_scopes).forEach((realization_scope) => {
    store.commit(names.MUTATE_REALIZATION_SCOPE, realization_scope);
    store
      .dispatch(names.PATCH_REALIZATION_SCOPE, {
        realization_scope_id: realization_scope.id,
        item: { order: realization_scope.order },
      })
      // Si falla, se recarga para no dejar en pantalla un orden que no se guardó
      .catch(fetchScopes);
  });
}

function fetchScopes() {
  store.dispatch(names.FETCH_REALIZATION_SCOPES, {
    egress_profile_id: props.egress_profile_id,
  });
}

const show_form = ref(false);
const editing_scope = ref(null);

function openCreate() {
  editing_scope.value = null;
  show_form.value = true;
}

function openEdit(realization_scope) {
  editing_scope.value = realization_scope;
  show_form.value = true;
}

function askForDelete(realization_scope_id) {
  proxy
    .$swal({
      title: proxy.$t("reusable.common.deleteConfirmTitle", {
        entity: entityName.value,
      }),
      text: proxy.$t("reusable.common.deleteConfirmText"),
      type: "warning",
      showCancelButton: true,
    })
    .then((result) => {
      if (!result.value) return;
      store
        .dispatch(names.DELETE_REALIZATION_SCOPE, realization_scope_id)
        .then(() => {
          toast(entityName.value + " " + proxy.$t("reusable.common.deleted"));
        });
    });
}

fetchScopes();
</script>

<style scoped>
th {
  vertical-align: middle;
  font-weight: var(--kl-fw-bold);
  font-size: var(--primary-font-size) !important;
  background-color: var(--kl-color-accent-medium) !important;
  color: var(--kl-text-on-primary) !important;
}
td {
  vertical-align: middle;
  background-color: var(--kl-surface) !important;
  color: var(--kl-text-body) !important;
  font-size: var(--primary-font-size) !important;
}
.description-cell {
  white-space: pre-line;
}
.grabbable {
  cursor: grab;
}
</style>
